import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Flame, Minus, Plus } from "lucide-react";
import FeedPostCard from "@/components/FeedPostCard";
import { feedHeaderBg } from "@/components/FeedGlow";

const COUNT_OPTIONS = [3, 5, 10, 25, 50];
const COLLAPSE_KEY = "tsi-collapse-trending";

/**
 * "Trending this week" — top board posts (by reactions, newest breaks ties) from
 * listings the user follows. While the site is young, ALL recent posts show (no
 * engagement required); engaged posts rise to the top. Same full card as the board.
 */
export default function TrendingStrip({ compact = false }) {
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(5);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
    });
    useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ } }, [collapsed]);
    useEffect(() => {
        api.get("/feed", { params: { limit, sort: "top" } })
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, [limit]);
    if (items.length === 0) return null;
    return (
        <div className={compact ? "" : "mb-8"} data-testid="trending-strip">
            <div
                className="sticky z-30 -mx-3 md:-mx-4 px-3 md:px-4 py-2.5 flex items-center justify-between gap-3 mb-4 flex-wrap"
                style={{ top: "var(--tsi-sticky-top, 64px)", background: feedHeaderBg("trending") }}
                data-testid="trending-strip-header"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <Link
                        to="/trending"
                        data-testid="trending-strip-heading-link"
                        className={`font-display tracking-wide inline-flex items-center gap-2 text-[#ff4500] hover:text-[#ff5722] transition-colors ${compact ? "text-2xl" : "text-3xl"}`}
                    >
                        <Flame className="h-5 w-5" /> Trending this week
                    </Link>
                    <button
                        type="button"
                        data-testid="trending-collapse-toggle"
                        onClick={() => setCollapsed((v) => !v)}
                        aria-label={collapsed ? "Expand trending posts" : "Collapse trending posts"}
                        className="inline-flex items-center justify-center h-6 w-6 shrink-0 text-[#ff4500] hover:opacity-70 transition-opacity"
                    >
                        {collapsed ? <Plus className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                    </button>
                </div>
                <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                    <select
                        data-testid="trending-count-select"
                        value={limit}
                        onChange={(e) => setLimit(Number(e.target.value))}
                        className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none focus:outline-none"
                    >
                        {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
            </div>
            {!collapsed && (
                <div className="space-y-4" data-testid="trending-items">
                    {items.map((p) => <FeedPostCard key={`${p._src || "board"}-${p.id}`} item={p} />)}
                </div>
            )}
        </div>
    );
}
